import { Component, inject, Input } from '@angular/core';
import { STATUS } from '../../common/use-loading.model';
import { TableConfig } from '../../table/model';
import { IEoDetailItemConfig } from '../eo-detail-item/eo-detail-item-config.model';
import { IEoDetailPanelConfig } from './eo-detail-panel-config.model';
import { EoDetailPanelService } from './eo-detail-panel.service';

@Component({
  selector: 'eo-detail-panel',
  templateUrl: './eo-detail-panel.component.html',
})
export class EoDetailPanelComponent<T> {
  viewPanelService = inject(EoDetailPanelService<T>);
  private _value?: T | T[];
  @Input() set value(val: T | T[] | undefined) {
    this._value = val;
  }
  get value(): T | undefined {
    if ((this._value as T) != undefined) return this._value as T;
    return undefined;
  }

  get tableValue(): T[] | undefined {
    if ((this._value as T[])?.length != undefined) return this._value as T[];
    return [];
  }

  private _config?: IEoDetailPanelConfig<T>;
  @Input() set config(val: IEoDetailPanelConfig<T> | undefined) {
    this._config = val;
    if (this._config?.tableConfig) this.tableConfig = this._config.tableConfig;
    if (this._config?.autoFetch) this.fetchData();
  }
  get config(): IEoDetailPanelConfig<T> | undefined {
    return this._config;
  }

  private _tableConfig?: TableConfig<T>;
  @Input() set tableConfig(val: TableConfig<T> | undefined) {
    this._tableConfig = val;
  }
  get tableConfig(): TableConfig<T> | undefined {
    return this._tableConfig ?? this.config?.tableConfig;
  }

  private _valueId?: string;
  @Input() set valueId(val: string | undefined) {
    this._valueId = val;
    if (val) this.fetchData(this.valueId);
  }
  get valueId(): string | undefined {
    return this._valueId;
  }

  private _loading = false;
  @Input() set loading(val: boolean) {
    this._loading = val;
  }
  get loading(): boolean {
    return this.config?.apiUrl ? this.fetchLoading : this._loading;
  }

  get fetchLoading(): boolean {
    return this.viewPanelService.status$.getValue() === STATUS.loading;
  }

  get panelStyleClass(): string {
    return this.config?.panelStyleClass || 'bg-white shadow-1 border-round-xl';
  }

  get itemContainerStyleClass(): string {
    return this.config?.itemContainerStyleClass || 'grid';
  }

  get itemStyleClass(): string {
    return 'col-12 md:col-6 lg:col-4 p-2';
  }

  private _headerMenuButtonIcon?: string;
  @Input() set headerMenuButtonIcon(val: string | undefined) {
    this._headerMenuButtonIcon = val;
  }
  get headerMenuButtonIcon(): string | undefined {
    return this._headerMenuButtonIcon ?? this.config?.headerMenuButtonIcon;
  }

  private _headerMenuButtonStyleClass?: string;
  @Input() set headerMenuButtonStyleClass(val: string | undefined) {
    this._headerMenuButtonStyleClass = val;
  }
  get headerMenuButtonStyleClass(): string | undefined {
    return (
      this._headerMenuButtonStyleClass ??
      this.config?.headerMenuButtonStyleClass
    );
  }

  private _headerMenuButtonLabel?: string;
  @Input() set headerMenuButtonLabel(val: string | undefined) {
    this._headerMenuButtonLabel = val;
  }
  get headerMenuButtonLabel(): string {
    return (
      this._headerMenuButtonLabel ?? this.config?.headerMenuButtonLabel ?? ''
    );
  }

  hasVisibleMenuButtonItems(): boolean {
    const items = this.config?.headerMenuItems;
    if (!items || items.length === 0) return false;
    return items.some((item) =>
      typeof item.isVisible === 'function'
        ? item.isVisible(this.value)
        : item.isVisible !== false
    );
  }

  getMenuItems(): any[] {
    const items = this.config?.headerMenuItems;
    if (!items || items.length === 0) return [];
    return items
      .filter((item) =>
        typeof item.isVisible === 'function'
          ? item.isVisible(this.value)
          : item.isVisible !== false
      )
      .map((item) => {
        const menuItem: any = { ...item };
        delete menuItem.isVisible;
        return menuItem;
      });
  }

  getItemValue(propName: string): any {
    return (this.value as any)?.[propName];
  }

  getItemVisible(item: IEoDetailItemConfig): boolean {
    if (typeof item.visible === 'function') {
      return item.visible(this.value) !== false;
    }
    return item.visible !== false;
  }

  fetchData(valueId?: string): void {
    if (!this.config?.apiUrl) return;

    this.viewPanelService
      .fetch(this.config.useMockApi ?? false, this.config.apiUrl, valueId)
      .subscribe((data) => this.onFetchSuccess(data));
  }

  onFetchSuccess(data: T | T[] | null): void {
    if (this.tableConfig) {
      this.tableConfig.Data = data as T[];
      this.value = data as T[];
    } else {
      this.value = (data as T) || undefined;
    }
  }
}
